import React from "react";
import useTranslate from "../../hooks/useTranslate";
// Reuse the shimmer/spinner keyframes the What's New feed already ships —
// same visual language, no duplicate CSS (Reuse-First).
import "../whats-new/whats-new.css";

// Chat-bubble icon from the design frames (1d empty state / FAB).
export const ChatIcon = ({ size = 24, stroke = "#9CA3AF", strokeWidth = 1.2 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M2 3.5A1.5 1.5 0 013.5 2h9A1.5 1.5 0 0114 3.5v6a1.5 1.5 0 01-1.5 1.5H8l-3 2.5V11H3.5A1.5 1.5 0 012 9.5z"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinejoin="round"
    />
  </svg>
);

// Loading skeleton — FeedStates.js pattern (tp-shimmer bars) shaped like a
// lounge post card: avatar circle, header line, two text lines, vote pill.
const Bar = ({ className }) => <div className={`tp-shimmer ${className}`} />;

const PostSkeleton = () => (
  <div className="bg-white border border-[#E4E9F0] rounded-[14px] p-[14px] sm:p-[18px_20px] flex gap-3">
    <Bar className="w-[34px] h-[34px] sm:w-[38px] sm:h-[38px] rounded-full shrink-0" />
    <div className="flex-1 min-w-0">
      <Bar className="h-4 w-[46%] rounded-md" />
      <Bar className="h-3 w-full mt-3 rounded-md" />
      <Bar className="h-3 w-[82%] mt-2 rounded-md" />
      <Bar className="h-[30px] w-[92px] mt-[14px] rounded-full" />
    </div>
  </div>
);

export const LoungeSkeleton = () => (
  <div className="flex flex-col gap-2.5 sm:gap-3" data-testid="lounge-skeleton">
    <PostSkeleton />
    <PostSkeleton />
    <PostSkeleton />
  </div>
);

// Error card — WhatsNew.js error-state pattern, lounge copy.
export const LoungeError = () => {
  const { t } = useTranslate();
  return (
    <div className="bg-white border border-[#E4E9F0] rounded-[14px] py-12 px-6 text-center">
      <p className="m-0 text-[15px] font-semibold text-[#111827]">
        {t("Couldn't load the lounge")}
      </p>
      <p className="m-0 mt-1.5 text-[13.5px] text-[#6B7280]">
        {t("Please try again in a moment.")}
      </p>
    </div>
  );
};

// Empty state — frame 1d ("The lounge is quiet." with the composer above it).
export const LoungeEmpty = () => {
  const { t } = useTranslate();
  return (
    <div className="bg-white border border-[#E4E9F0] rounded-[14px] py-11 px-7 sm:py-14 sm:px-10 flex flex-col items-center text-center">
      <span className="w-[52px] h-[52px] sm:w-14 sm:h-14 rounded-full bg-[#F1F5FA] flex items-center justify-center">
        <ChatIcon size={24} />
      </span>
      <p className="m-0 mt-3.5 sm:mt-4 text-[15px] font-semibold text-[#111827]">
        {t("The lounge is quiet.")}
      </p>
      <p className="m-0 mt-[5px] text-[13.5px] text-[#6B7280]">
        {t("Start the first discussion.")}
      </p>
    </div>
  );
};

// Channel-filter empty state — frame 1p ("Nothing in {channel} yet." with a
// clear-filter pill).
export const ChannelEmpty = ({ channelName, onClear }) => {
  const { t, tv } = useTranslate();
  return (
    <div className="bg-white border border-[#E4E9F0] rounded-[14px] p-10 flex flex-col items-center text-center">
      <p className="m-0 text-[14.5px] font-semibold text-[#111827]">
        {tv("Nothing in {{channel}} yet.", { channel: channelName })}
      </p>
      <p className="m-0 mt-[5px] text-[13px] text-[#6B7280]">
        {t("Start the first discussion, or clear the filter.")}
      </p>
      <button
        type="button"
        onClick={onClear}
        className="mt-3.5 bg-white border border-[#DDE3EB] rounded-full py-2 px-[18px] min-h-[44px] sm:min-h-0 text-[13px] font-semibold text-[#6B7280] hover:border-[#C6CFDA] hover:text-[#111827] whitespace-nowrap"
      >
        {t("Show all channels")}
      </button>
    </div>
  );
};

// "Load older posts" pill — frame 1a pagination control.
export const LoadOlderButton = ({ loading, onClick }) => {
  const { t } = useTranslate();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={[
        "self-center mt-1.5 flex items-center gap-2 bg-white border border-[#DDE3EB] rounded-full py-[9px] px-[22px] min-h-[44px] sm:min-h-0 text-[13px] font-semibold whitespace-nowrap",
        loading
          ? "text-[#9CA3AF] cursor-default"
          : "text-[#6B7280] hover:border-[#C6CFDA] hover:text-[#111827]",
      ].join(" ")}
    >
      {loading && (
        <span className="tp-spin w-3.5 h-3.5 rounded-full border-2 border-[#C7D6E8] border-t-[#2283FF] box-border" />
      )}
      {loading ? t("Loading older posts…") : t("Load older posts")}
    </button>
  );
};
